import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { hrAPI } from '../../../api/hr';
import { MONTH_OPTIONS, PAYROLL_STATUSES, PAYMENT_METHODS, YEAR_OPTIONS } from '../constants';
import { toFiniteNumber, toISOStringOrNull } from '../utils';

const defaultCreateState = {
  user: '',
  salary: '',
  month: new Date().getMonth() + 1,
  year: new Date().getFullYear(),
  workingDays: 30,
  presentDays: 30,
  absentDays: 0,
  leaveDays: 0,
  halfDays: 0,
  overtimeHours: 0,
  totalWorkingHours: 0, // For hourly workers
  bonus: 0,
  remarks: ''
};

const defaultUpdateState = {
  paymentStatus: 'Pending',
  paymentMethod: 'Bank Transfer',
  paymentDate: '',
  transactionId: '',
  remarks: ''
};

const PayrollFormModal = ({
  open,
  mode = 'create',
  initialData,
  employeeOptions = [],
  salaryOptions = [],
  employeesLoading = false,
  salariesLoading = false,
  onClose,
  onSubmit
}) => {
  const initialState = useMemo(() => {
    if (mode === 'edit' && initialData) {
      return {
        paymentStatus: initialData.paymentStatus || 'Pending',
        paymentMethod: initialData.paymentMethod || 'Bank Transfer',
        paymentDate: initialData.paymentDate ? new Date(initialData.paymentDate).toISOString().slice(0, 16) : '',
        transactionId: initialData.transactionId || '',
        remarks: initialData.remarks || ''
      };
    }
    if (mode === 'create') {
      return {
        ...defaultCreateState,
        user: initialData?.user?._id || '',
        salary: initialData?.salary?._id || ''
      };
    }
    return defaultUpdateState;
  }, [initialData, mode]);

  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [autoCalculated, setAutoCalculated] = useState(false);
  const [calculatedData, setCalculatedData] = useState(null);
  const [selectedSalaryData, setSelectedSalaryData] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(initialState);
      setAutoCalculated(false);
      setCalculatedData(null);
      setSelectedSalaryData(null);
    }
  }, [initialState, open]);

  // Get selected salary data for payment frequency check
  useEffect(() => {
    if (form.salary && salaryOptions.length > 0) {
      const salary = salaryOptions.find(s => s.value === form.salary);
      setSelectedSalaryData(salary);
      
      // Adjust working days based on payment frequency
      if (salary?.paymentFrequency && mode === 'create') {
        let defaultDays = 30;
        if (salary.paymentFrequency === 'Bi-Weekly') {
          defaultDays = 10; // 2 weeks
        } else if (salary.paymentFrequency === 'Weekly') {
          defaultDays = 5; // 1 week
        }
        setForm(prev => ({ ...prev, workingDays: defaultDays }));
      }
    } else {
      setSelectedSalaryData(null);
    }
  }, [form.salary, salaryOptions, mode]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: value
    }));
    // Reset auto-calculated flag when user manually changes values
    if (mode === 'create' && autoCalculated && ['presentDays', 'absentDays', 'leaveDays', 'halfDays', 'overtimeHours', 'bonus'].includes(name)) {
      setAutoCalculated(false);
    }
  };

  const handleCalculate = async () => {
    if (!form.user || !form.salary || !form.month || !form.year) {
      alert('Please select employee, salary profile, month, and year first');
      return;
    }

    setCalculating(true);
    try {
      const payload = {
        user: form.user,
        salary: form.salary,
        month: Number(form.month),
        year: Number(form.year),
        workingDays: toFiniteNumber(form.workingDays, 30)
      };

      const result = await hrAPI.calculatePayroll(payload);
      if (result) {
        setCalculatedData(result);
        setForm((prev) => ({
          ...prev,
          workingDays: result.workingDays || prev.workingDays,
          presentDays: result.presentDays || 0,
          absentDays: result.absentDays || 0,
          leaveDays: result.leaveDays || 0,
          halfDays: result.halfDays || 0,
          overtimeHours: result.overtimeHours || 0,
          totalWorkingHours: result.totalWorkingHours || 0 // For hourly workers
        }));
        setAutoCalculated(true);
        
        // Show warning if no attendance records found
        if (result.totalAttendanceRecords === 0) {
          const monthName = MONTH_OPTIONS.find(m => m.value === Number(form.month))?.label || form.month;
          alert(`Warning: No attendance records found for ${monthName} ${form.year}. All values have been set to 0. You can manually enter the attendance data.`);
        }
      }
    } catch (error) {
      console.error('Error calculating payroll:', error);
      alert(error.message || 'Failed to calculate payroll');
    } finally {
      setCalculating(false);
    }
  };

  const filteredSalaryOptions = useMemo(() => {
    if (!form.user) {
      return salaryOptions;
    }
    const matches = salaryOptions.filter((option) => option.user === form.user);
    return matches.length ? matches : salaryOptions;
  }, [form.user, salaryOptions]);

  if (!open) {
    return null;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = {};

    if (mode === 'create') {
      if (!form.user || !form.salary) {
        return;
      }
      Object.assign(payload, {
        user: form.user,
        salary: form.salary,
        month: Number(form.month),
        year: Number(form.year),
        workingDays: toFiniteNumber(form.workingDays, 0),
        presentDays: toFiniteNumber(form.presentDays, 0),
        absentDays: toFiniteNumber(form.absentDays, 0),
        leaveDays: toFiniteNumber(form.leaveDays, 0),
        halfDays: toFiniteNumber(form.halfDays, 0),
        overtimeHours: toFiniteNumber(form.overtimeHours, 0),
        totalWorkingHours: toFiniteNumber(form.totalWorkingHours, 0), // For hourly workers
        bonus: toFiniteNumber(form.bonus, 0),
        remarks: form.remarks?.trim() || undefined
      });
    } else {
      Object.assign(payload, {
        paymentStatus: form.paymentStatus,
        paymentMethod: form.paymentMethod,
        paymentDate: toISOStringOrNull(form.paymentDate),
        transactionId: form.transactionId?.trim() || undefined,
        remarks: form.remarks?.trim() || undefined
      });
    }

    setSubmitting(true);
    try {
      const success = await onSubmit?.(payload);
      if (success) {
        onClose?.();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {mode === 'edit' ? 'Update Payroll Status' : 'Generate Payroll'}
            </h3>
            <p className="text-sm text-gray-500">
              {mode === 'edit' ? 'Confirm payouts and payment references' : 'Auto-calculate from attendance, review & adjust, then generate'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-6 space-y-6">
          {mode === 'create' ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
                  <select
                    name="user"
                    value={form.user}
                    onChange={handleChange}
                    disabled={employeesLoading}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  >
                    <option value="">
                      {employeesLoading ? 'Loading employees...' : 'Select employee'}
                    </option>
                    {employeeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Salary Profile *</label>
                  <select
                    name="salary"
                    value={form.salary}
                    onChange={handleChange}
                    disabled={salariesLoading}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  >
                    <option value="">
                      {salariesLoading ? 'Loading salaries...' : 'Select salary record'}
                    </option>
                    {filteredSalaryOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Payment Frequency Info */}
              {selectedSalaryData?.paymentFrequency && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <Icon name="Clock" size={18} className="text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-amber-900">Payment Frequency: {selectedSalaryData.paymentFrequency}</h4>
                      <p className="text-xs text-amber-700 mt-1">
                        {selectedSalaryData.paymentFrequency === 'Monthly' && 
                          'This employee receives monthly salary. Select the month and year for payroll generation.'}
                        {selectedSalaryData.paymentFrequency === 'Bi-Weekly' && 
                          'This employee receives bi-weekly salary (every 2 weeks). Select the month containing the pay period.'}
                        {selectedSalaryData.paymentFrequency === 'Weekly' && 
                          'This employee receives weekly salary. Select the month containing the pay week.'}
                      </p>
                      {selectedSalaryData.paymentFrequency !== 'Monthly' && (
                        <p className="text-xs text-amber-600 font-medium mt-1">
                          ⚠️ Note: For {selectedSalaryData.paymentFrequency} frequency, working days will be prorated automatically.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Month *</label>
                  <select
                    name="month"
                    value={form.month}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    {MONTH_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year *</label>
                  <select
                    name="year"
                    value={form.year}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    {YEAR_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Working Days *</label>
                  <input
                    type="number"
                    name="workingDays"
                    min="0"
                    max="31"
                    value={form.workingDays}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              {/* Auto-Calculate Button */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-1">
                      <Icon name="Calculator" size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">Auto-Calculate from Attendance</h4>
                      <p className="text-xs text-gray-600 mt-0.5">
                        Fetch attendance records for the selected month and auto-populate attendance fields.
                        {calculatedData && (
                          <span className="text-green-600 font-medium ml-1">
                            ({calculatedData.totalAttendanceRecords} records found)
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleCalculate}
                    disabled={calculating || !form.user || !form.salary}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {calculating ? (
                      <>
                        <Icon name="Loader2" size={16} className="animate-spin" />
                        <span>Calculating...</span>
                      </>
                    ) : (
                      <>
                        <Icon name="Calculator" size={16} />
                        <span>Calculate</span>
                      </>
                    )}
                  </button>
                </div>
                {autoCalculated && (
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <div className="flex items-center space-x-2 text-xs text-green-700">
                      <Icon name="CheckCircle2" size={14} />
                      <span className="font-medium">
                        Auto-calculated from attendance. You can review and adjust the values below.
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    Present Days *
                    {autoCalculated && (
                      <span className="ml-2 text-xs text-green-600 flex items-center">
                        <Icon name="Sparkles" size={12} className="mr-1" />
                        Auto
                      </span>
                    )}
                  </label>
                  <input
                    type="number"
                    name="presentDays"
                    min="0"
                    max="31"
                    value={form.presentDays}
                    onChange={handleChange}
                    className={`w-full border rounded-lg px-3 py-2 ${autoCalculated ? 'border-green-300 bg-green-50' : 'border-gray-300'}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    Absent Days
                    {autoCalculated && (
                      <span className="ml-2 text-xs text-green-600 flex items-center">
                        <Icon name="Sparkles" size={12} className="mr-1" />
                        Auto
                      </span>
                    )}
                  </label>
                  <input
                    type="number"
                    name="absentDays"
                    min="0"
                    max="31"
                    value={form.absentDays}
                    onChange={handleChange}
                    className={`w-full border rounded-lg px-3 py-2 ${autoCalculated ? 'border-green-300 bg-green-50' : 'border-gray-300'}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    Leave Days
                    {autoCalculated && (
                      <span className="ml-2 text-xs text-green-600 flex items-center">
                        <Icon name="Sparkles" size={12} className="mr-1" />
                        Auto
                      </span>
                    )}
                  </label>
                  <input
                    type="number"
                    name="leaveDays"
                    min="0"
                    max="31"
                    value={form.leaveDays}
                    onChange={handleChange}
                    className={`w-full border rounded-lg px-3 py-2 ${autoCalculated ? 'border-green-300 bg-green-50' : 'border-gray-300'}`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    Half Days
                    {autoCalculated && (
                      <span className="ml-2 text-xs text-green-600 flex items-center">
                        <Icon name="Sparkles" size={12} className="mr-1" />
                        Auto
                      </span>
                    )}
                  </label>
                  <input
                    type="number"
                    name="halfDays"
                    min="0"
                    max="31"
                    value={form.halfDays}
                    onChange={handleChange}
                    className={`w-full border rounded-lg px-3 py-2 ${autoCalculated ? 'border-green-300 bg-green-50' : 'border-gray-300'}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    Overtime Hours
                    {autoCalculated && (
                      <span className="ml-2 text-xs text-green-600 flex items-center">
                        <Icon name="Sparkles" size={12} className="mr-1" />
                        Auto
                      </span>
                    )}
                  </label>
                  <input
                    type="number"
                    name="overtimeHours"
                    min="0"
                    value={form.overtimeHours}
                    onChange={handleChange}
                    className={`w-full border rounded-lg px-3 py-2 ${autoCalculated ? 'border-green-300 bg-green-50' : 'border-gray-300'}`}
                  />
                </div>
                <div className="md:col-span-1">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                    <div className="flex items-start">
                      <Icon name="Info" size={14} className="mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        Overtime pay is auto-calculated at 2× the normal hourly rate per Indian Labour Code.
                        Enter hours only; the amount is computed on submit.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bonus</label>
                  <input
                    type="number"
                    name="bonus"
                    min="0"
                    value={form.bonus}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                  <input
                    type="text"
                    name="remarks"
                    value={form.remarks}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="Optional"
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status *</label>
                  <select
                    name="paymentStatus"
                    value={form.paymentStatus}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    {PAYROLL_STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                  <select
                    name="paymentMethod"
                    value={form.paymentMethod}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    {PAYMENT_METHODS.map((method) => (
                      <option key={method} value={method}>
                        {method}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                  <input
                    type="datetime-local"
                    name="paymentDate"
                    value={form.paymentDate}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Transaction ID</label>
                  <input
                    type="text"
                    name="transactionId"
                    value={form.transactionId}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="Reference number"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  name="remarks"
                  value={form.remarks}
                  onChange={handleChange}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            </>
          )}

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 flex items-center space-x-2"
            >
              {submitting ? (
                <>
                  <Icon name="Loader2" size={16} className="animate-spin" />
                  <span>Saving...</span>
                </>
              ) : mode === 'edit' ? (
                <>
                  <Icon name="BadgeCheck" size={16} />
                  <span>Update Payment</span>
                </>
              ) : (
                <>
                  <Icon name="FileCheck" size={16} />
                  <span>Generate & Save Payroll</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PayrollFormModal;
